import { ReactElement, useEffect, useMemo } from "react";

import { Box, HStack, Spacer, Stack, Text } from "@chakra-ui/react";

import { InvoiceModel } from "api";
import BackToTopFAB from "components/BackToTopFAB";
import EmptyWithClearButton from "components/EmptyWithClearButton";
import LoadingSpinner from "components/LoadingSpinner";
import SearchBoxWithIcon from "components/SearchBoxWithIcon";
import { BACK_TO_TOP_MIN_LINES } from "components/SortableTable/SortableDynamicTable";
import FileDownloadLink from "components/tableColumns/FileDownloadLink";
import { ActiveSort, useRequestParams } from "contexts/RequestParamsProvider";
import useContractDetails from "hooks/api/contracts/useContractDetails";
import useInvoiceFiles from "hooks/api/invoices/useInvoiceFiles";
import useInvoices from "hooks/api/invoices/useInvoices";
import { WarningIcon } from "icons";
import { removeNullable } from "services/apiHelpers";
import { formatDate } from "util/statHelpers";
import { detailsBodyPadding } from "util/styleHelpers";
import ContractDetailsFiltersSection, {
  FilterSectionStickyHeader,
} from "../filters/ContractDetailsFiltersSection";
import InvoicesTable, { invoicesColumns } from "./InvoicesTable";

export const isInvoiceOverDue = (
  amountDue: number,
  dueDate: Date | string
): boolean =>
  dueDate instanceof Date && dueDate.getTime() < Date.now() && amountDue > 0;

const InvoicesTab = ({
  toggleIndicator,
  setErrorMessage,
}: DetailsTabProps): ReactElement => {
  const {
    filters,
    searchTerm,
    onChangeSearchTerm,
    activeSort,
    hasSelectedFilters,
    clearFilters,
    clearSearchTerm,
  } = useRequestParams();

  const { data: contract, error: contractDetailsError } = useContractDetails();

  useEffect(() => {
    if (contractDetailsError && setErrorMessage) {
      setErrorMessage((contractDetailsError as Error).message);
    }
  }, [setErrorMessage, contractDetailsError]);

  const {
    data: invoices,
    isLoading,
    isFetching,
    error: invoicesListError,
  } = useInvoices(
    contract?.contractNumber,
    searchTerm,
    filters as InvoiceFilters,
    activeSort as ActiveSort<InvoiceModel>
  );

  useEffect(() => {
    if (invoicesListError && setErrorMessage) {
      setErrorMessage((invoicesListError as Error).message);
    }
  }, [setErrorMessage, invoicesListError]);

  useEffect(() => {
    toggleIndicator?.(
      invoices?.find((value) =>
        isInvoiceOverDue(value.amountDue, value.dueDate)
      ) !== undefined
    );
  }, [toggleIndicator, invoices]);

  const { mutateAsync } = useInvoiceFiles();

  const invoicesTableEntries = useMemo(
    () =>
      invoices?.map((value) => {
        const formattedDueDate =
          value.dueDate instanceof Date
            ? formatDate(new Date(value.dueDate))
            : value.dueDate;
        return {
          ...value,
          invoiceNumber: (
            <FileDownloadLink
              label={value.invoiceNumber}
              onFetchFiles={() =>
                mutateAsync({
                  docEntry: value.docEntry,
                  isCredit: value.isCredit,
                })
              }
              docEntry={value.docEntry}
            />
          ),
          amountDue: value.isCredit
            ? `(${value.amountDueFormatted}) ${value.currency}`
            : `${value.amountDueFormatted} ${value.currency}`,
          dueDate: isInvoiceOverDue(value.amountDue, value.dueDate) ? (
            <HStack spacing={1} align="center">
              <Text color="warningRed" fontWeight="semibold" as="span">
                {formattedDueDate}
              </Text>
              <WarningIcon />
            </HStack>
          ) : (
            formattedDueDate
          ),
        };
      }) ?? [],
    [invoices, mutateAsync]
  );

  const invoicesList = useMemo(() => {
    if (!(invoices && !(isFetching || isLoading))) {
      return <LoadingSpinner />;
    }

    const emptyOnClick = hasSelectedFilters ? clearFilters : clearSearchTerm;
    const emptyMessage = `Clear ${
      hasSelectedFilters ? "all filters" : "search"
    }`;

    return invoices.length > 0 ? (
      <BackToTopFAB isVisible={invoices.length > BACK_TO_TOP_MIN_LINES * 2}>
        <InvoicesTable invoices={invoicesTableEntries} />
      </BackToTopFAB>
    ) : (
      <EmptyWithClearButton
        message="No invoices yet"
        hasFilters={hasSelectedFilters}
        hasSearch={!!searchTerm}
        onClick={emptyOnClick}
        buttonText={emptyMessage}
        buttonVisible={hasSelectedFilters || !!searchTerm}
      />
    );
  }, [
    hasSelectedFilters,
    invoices,
    isFetching,
    isLoading,
    clearFilters,
    clearSearchTerm,
    invoicesTableEntries,
    searchTerm,
  ]);

  return (
    <Stack spacing={4} pt={detailsBodyPadding}>
      <FilterSectionStickyHeader>
        <Box>
          <SearchBoxWithIcon value={searchTerm} onChange={onChangeSearchTerm} />
        </Box>
        <Spacer flex={1} />
        <ContractDetailsFiltersSection
          filterDefinitions={[]}
          toggles={[
            {
              filterKey: "dueDate",
              label: "Past Due",
            },
            {
              filterKey: "amountDue",
              label: "Amount Due",
            },
          ]}
          sortOptions={removeNullable(
            invoicesColumns.map((column) =>
              column.sortKey && column.columnLabel
                ? {
                    label: column.columnLabel,
                    sortKey: column.sortKey,
                  }
                : null
            )
          )}
        />
      </FilterSectionStickyHeader>
      {invoicesList}
    </Stack>
  );
};

export default InvoicesTab;
